import { GeneralResMock } from '../types/general.type'
import { HistoryResMock, MedDataMock } from '../types/history.type'
import { IntakeDataMock, IntakeIncludedMock } from '../types/intake.type'
import { PatientIncluded, PatientSAttrProps } from '../types/patient.types'
import { ImagingResultResponse, LabResultResponse } from '../types/result.type'
import { VitalDataMock, VitalResMock } from '../types/vital.type'
import { getProblemLabel } from '../utils/problemLabel'
import AXIOS_INSTANCE from './axiosInstance'

// Extra query params (e.g. `{ 'page[size]': 100 }` — the API's JSON:API pagination, default 20, max 100).
export type ListParams = Record<string, string | number>

export const hpiNote = {
  fetchCC: async (encounter_id: string) => {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<IntakeDataMock[], IntakeIncludedMock[]>>(
      `intakes?encounter_id=${encounter_id}`
    )
    return data.data
  },
  fetchHistory: async (
    patientId: string,
    params?: ListParams,
    opts?: { encounterId?: string | number; excludeEncounterId?: string | number }
  ) => {
    const mergedParams: ListParams = {
      ...(params ?? {}),
      ...(opts?.encounterId !== undefined && { encounter_id: opts.encounterId }),
      ...(opts?.excludeEncounterId !== undefined && { exclude_encounter_id: opts.excludeEncounterId })
    }
    const { data } = await AXIOS_INSTANCE.get<HistoryResMock<MedDataMock[]>>(
      `/medical_histories?patient_id=${patientId}`,
      Object.keys(mergedParams).length ? { params: mergedParams } : undefined
    )
    return data.data.map(history => ({ ...history, label: getProblemLabel(history, data.included) }))
  },
  fetchImagingResult: async (encounterId: string, params?: ListParams) => {
    const { data } = await AXIOS_INSTANCE.get<ImagingResultResponse>(
      `imaging_results?encounter_id=${encounterId}`,
      params ? { params } : undefined
    )
    return data
  },
  fetchLabResult: async (encounterId: string, params?: ListParams) => {
    const { data } = await AXIOS_INSTANCE.get<LabResultResponse>(
      `lab_results?encounter_id=${encounterId}`,
      params ? { params } : undefined
    )
    return data
  },
  fetchPatient: async (patientId: string) => {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<PatientSAttrProps, PatientIncluded[]>>(
      `patients/${patientId}`
    )
    return data.data.attributes
  },
  fetchVital: async (patientId: string, payload: string) => {
    const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(
      `/vital_signs?patient_id=${patientId}&vital_type[]=${payload}`
    )
    return data.data
  },
  fetchVitals: async (patientId: string, encounter_id?: string, intake_id?: string) => {
    const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(`/vital_signs`, {
      params: {
        ...(patientId && { patient_id: patientId }),
        ...(encounter_id && { encounter_id }),
        ...(intake_id && { intake_id })
      }
    })
    return data.data
  },
  // This encounter's vitals, newest first. No patient_id on purpose: the API ignores encounter_id/intake_id
  // whenever patient_id is present (VitalSigns::FilterQuery) and would return the patient's 20 oldest rows.
  // With no patient_id it matches encounter_id OR intake_id.
  fetchEncounterVitals: async (encounterId: string, intakeId?: string) => {
    const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(`/vital_signs`, {
      params: {
        encounter_id: encounterId,
        ...(intakeId && { intake_id: intakeId }),
        'page[size]': 100,
        sort_by: 'created_at',
        sort_dir: 'desc'
      }
    })
    return data.data
  },
  // The patient's most recent readings of one vital type, newest first.
  fetchRecentVitalsOfType: async (patientId: string, vitalType: string, size = 20) => {
    const { data } = await AXIOS_INSTANCE.get<VitalResMock<VitalDataMock[]>>(`/vital_signs`, {
      params: {
        patient_id: patientId,
        'page[size]': size,
        sort_by: 'created_at',
        sort_dir: 'desc',
        'vital_type[]': vitalType
      }
    })
    return data.data
  }
}
